import React, { useCallback, useMemo, useReducer, useRef } from 'react';

import type { ParagraphElement } from '@aline/common';
import { isParagraphElement } from '@aline/common';
import { Editor, Node, Path } from 'slate';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import { useGetCurrentDoc, usePrevious } from 'src/hooks';
import { useDocContentChangedCount } from 'src/layout-engine/components/Slate';
import { useEditor, useEditorLoading, useSelection } from 'src/layout-engine/hooks';
import { ReactEditor } from 'src/layout-engine/react-editor';
import { makeStyles } from 'tss-react/mui';

import { DocumentHeadings } from './DocumentHeadings';
import { OutlineMenuHeader } from './OutlineMenuHeader';
import type { OutlineHeadingItem } from './types';
import { WorkflowOutlineView } from './WorkflowOutlineView';

const useStyles = makeStyles()(() => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingBottom: '16px',
    paddingLeft: '4px',
  },
}));

function isHeading(node: ParagraphElement) {
  return node.variant.startsWith('style-heading');
}

function useHeadingNodes(editor: Editor) {
  const { docContentChangedCount } = useDocContentChangedCount();
  const editorLoading = useEditorLoading();
  const selection = useSelection();
  const headingsSnapshot = useRef<OutlineHeadingItem[] | null>(null);
  const [refreshHeadingsKey, refreshHeadings] = useReducer(x => 1 - x, 0);

  const headings = useMemo(() => {
    if (editorLoading) return headingsSnapshot.current;

    const items = Array.from(
      Editor.nodes<ParagraphElement>(editor, {
        at: [],
        match: node => isParagraphElement(node) && isHeading(node),
      })
    )
      .map(([node, path]) => ({
        label: Node.string(node).trim(),
        path,
        variant: node.variant,
      }))
      .filter(({ label }) => label !== '');

    headingsSnapshot.current = items;

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docContentChangedCount, editor, editorLoading, refreshHeadingsKey]);

  const selectedHeadingIndex = useMemo(() => {
    if (!selection || !headings) return null;
    return headings.findLastIndex(
      ({ path }) =>
        Path.isDescendant(selection.anchor.path, path) || Path.isAfter(selection.anchor.path, path)
    );
  }, [headings, selection]);

  /**
   * Since the set of headings is only recomputed when docContentChangedCount is updated, but the
   * selectedHeadingIndex is recomputed immediately on selection change, changes to heading paths
   * can briefly cause selectedHeadingIndex to be incorrect. To remedy this, update the set of
   * headings immediately when the apparent selected heading changes.
   */
  const prevSelectedHeadingIndex = usePrevious(selectedHeadingIndex);
  if (selectedHeadingIndex !== prevSelectedHeadingIndex) refreshHeadings();

  return { headings, selectedHeadingIndex };
}

interface Props {
  areHeadingsSupported: boolean;
  handleClose: () => void;
}

export function OutlineMenu({ areHeadingsSupported, handleClose }: Props) {
  const editor = useEditor();
  const { classes } = useStyles();

  const { headings, selectedHeadingIndex } = useHeadingNodes(editor);
  const rWHeightPx = useEditorTabStateSelector(state => state.reactWindow.windowHeight);
  const rWOffsetPx = useEditorTabStateSelector(state => state.reactWindow.scrollOffset);

  const { documentId } = useDocumentParams();
  const { document } = useGetCurrentDoc();
  const { data } = Apollo.useGetDocumentNameQuery({
    variables: { documentId },
  });
  const documentName = data?.document?.name?.trim() || 'Untitled document';

  const handleScrollTo = useCallback(
    (heading: OutlineHeadingItem) => {
      ClientTransforms.scrollToLocation(editor, {
        cMLocation: Editor.start(editor, heading.path),
        rWHeightPx,
        rWOffsetPx,
      });
      ReactEditor.focus(editor);
    },
    [editor, rWHeightPx, rWOffsetPx]
  );

  return (
    <>
      <OutlineMenuHeader
        handleClose={handleClose}
        isWorkflow={!!document?.workflow}
        workflowId={document?.workflow ? document.workflow.id : null}
      />
      <div className={classes.wrapper}>
        {document?.workflow ? (
          <WorkflowOutlineView
            areHeadingsSupported={areHeadingsSupported}
            handleScrollTo={handleScrollTo}
            headings={headings}
            selectedHeadingIndex={selectedHeadingIndex}
            workflowId={document.workflow.id}
          />
        ) : (
          <DocumentHeadings
            areHeadingsSupported={areHeadingsSupported}
            document={document}
            documentName={documentName}
            handleScrollTo={handleScrollTo}
            headings={headings}
            isOpen={true}
            selectedHeadingIndex={selectedHeadingIndex}
          />
        )}
      </div>
    </>
  );
}
